import { useCallback, useRef, useState } from 'react';
import { placeConnectionMenu } from '../../../TaskNode/ConnectionMenu';
import { armClickOrDrag } from '../utils/armClickOrDrag';

function clientToCanvas(containerRef, panRef, zoomRef, clientX, clientY) {
  const rect = containerRef?.current?.getBoundingClientRect();
  if (!rect) return null;
  return {
    x: (clientX - rect.left - panRef.current.x) / zoomRef.current,
    y: (clientY - rect.top - panRef.current.y) / zoomRef.current,
  };
}

/**
 * Click vs drag on connection handles, and the add/insert menu that a click opens.
 */
export function useConnectionActions({
  containerRef,
  panRef,
  zoomRef,
  onConnectionDragStart,
  onNodeDragStart,
}) {
  const [connectionMenuTaskId, setConnectionMenuTaskId] = useState(null);
  const [connectionMenuPos, setConnectionMenuPos] = useState(null);
  /** Decision option id when opening the menu from a branch pill; null for mission + handle */
  const [connectionMenuBranch, setConnectionMenuBranch] = useState(null);
  /** 'after' is the bottom (+); 'before' is the top (+) */
  const [connectionMenuMode, setConnectionMenuMode] = useState('after');

  const menuElRef = useRef(null);
  const openTokenRef = useRef(0);

  const openConnectionMenu = useCallback((taskId, pos, { branch = null, mode = 'after' } = {}) => {
    setConnectionMenuBranch(branch);
    setConnectionMenuMode(mode);
    setConnectionMenuPos(pos);
    setConnectionMenuTaskId(taskId);
  }, []);

  const closeConnectionMenu = useCallback(() => {
    setConnectionMenuTaskId(null);
    setConnectionMenuPos(null);
    setConnectionMenuBranch(null);
    setConnectionMenuMode('after');
  }, []);

  // The menu is already on the page, hidden. Show it on the press, and let
  // React catch up after the browser has painted. Redrawing every handle
  // first is what made the menu wait.
  const revealMenu = useCallback((taskId, pos, options = {}) => {
    const mode = options.mode || 'after';
    const el = menuElRef.current;
    if (el) {
      const placed = placeConnectionMenu(pos, mode === 'before');
      el.style.left = `${placed.left}px`;
      el.style.top = `${placed.top}px`;
      const header = el.querySelector('.connection-menu__header');
      if (header) header.textContent = mode === 'before' ? 'Insert Before' : 'Add Connected Node';
      el.classList.remove('connection-menu--shut');
    }
    const token = ++openTokenRef.current;
    setTimeout(() => {
      if (openTokenRef.current !== token) return;
      openConnectionMenu(taskId, pos, options);
    }, 0);
  }, [openConnectionMenu]);

  const hideMenu = useCallback(() => {
    openTokenRef.current += 1;
    menuElRef.current?.classList.add('connection-menu--shut');
    closeConnectionMenu();
  }, [closeConnectionMenu]);

  const handleConnectionMouseDown = useCallback((task, e) => {
    e.stopPropagation();
    e.preventDefault();
    revealMenu(task.id, { x: e.clientX, y: e.clientY });

    armClickOrDrag(e, {
      onDrag: () => {
        if (task.nodeType === 'milestone' || task.nodeType === 'note') return false;
        hideMenu();
        const point = clientToCanvas(containerRef, panRef, zoomRef, e.clientX, e.clientY);
        if (point) onConnectionDragStart?.(task.id, e, null, point);
        return true;
      },
    });
  }, [containerRef, panRef, zoomRef, onConnectionDragStart, revealMenu, hideMenu]);

  const handleDecisionBranchMouseDown = useCallback((task, option, e) => {
    e.stopPropagation();
    e.preventDefault();
    revealMenu(task.id, { x: e.clientX, y: e.clientY }, { branch: option.id });

    armClickOrDrag(e, {
      onDrag: () => {
        hideMenu();
        const point = clientToCanvas(containerRef, panRef, zoomRef, e.clientX, e.clientY);
        if (point) onConnectionDragStart?.(task.id, e, option.id, point);
        return true;
      },
    });
  }, [containerRef, panRef, zoomRef, onConnectionDragStart, revealMenu, hideMenu]);

  const handleBeforeMouseDown = useCallback((task, e) => {
    e.stopPropagation();
    e.preventDefault();
    revealMenu(task.id, { x: e.clientX, y: e.clientY }, { mode: 'before' });
  }, [revealMenu]);

  const handleDragHandleMouseDown = useCallback((task, e) => {
    if (!onNodeDragStart) return;
    const point = clientToCanvas(containerRef, panRef, zoomRef, e.clientX, e.clientY);
    if (point) onNodeDragStart(task.id, e, point);
  }, [containerRef, panRef, zoomRef, onNodeDragStart]);

  return {
    connectionMenuTaskId,
    connectionMenuPos,
    connectionMenuBranch,
    connectionMenuMode,
    closeConnectionMenu,
    handleConnectionMouseDown,
    handleDecisionBranchMouseDown,
    handleBeforeMouseDown,
    handleDragHandleMouseDown,
    menuElRef,
  };
}
